import React, { ReactNode, useEffect, useState } from 'react';
import { Label, LabelText, TextInput } from '../../shared/BlockWidgets';
import { MultilinedText } from '../../shared/MultilinedText';
import { CommentButton } from '../../shared/Comment';
import styles from './MultipleChoice.module.css';
import { Inline } from 'components/layout';
import {
  MultipleChoiceBlock,
  isOtherOptionSelected,
} from 'concepts/Extraction/Blocks/MultipleChoice/MultipleChoiceBlock';
import { useDescribedBy } from 'hooks/useAccessibleName';
import { NakedButton } from 'system/base/Button';
import { useDebouncedFunction } from 'hooks/useDebouncedFunction';

export type ReadonlyMultipleChoiceProps = {
  block: MultipleChoiceBlock;
  radioGroupClassName?: string;
  radioGroupDescribedBy?: string;
  otherTextFieldDescribedBy?: string;
  trailingContent?: ReactNode;
};

export type EditableMultipleChoiceProps = ReadonlyMultipleChoiceProps & {
  editable: true;
  value: string | null;
  comments: string | null;
  onChange: (newValue: string | null) => void;
  onOtherFieldFocus?: () => void;
  onClickOption?: (optionId: string) => void;
  onOtherFieldClick?: () => void;
  onChangeComments: (newComments: string) => void;
  hideClearSelection?: boolean;
};

export type MultipleChoiceProps =
  | ReadonlyMultipleChoiceProps
  | EditableMultipleChoiceProps;

const isEditableMultipleChoiceProps = (
  props: MultipleChoiceProps
): props is EditableMultipleChoiceProps =>
  (props as EditableMultipleChoiceProps).editable;

type RenderCommentsProps = {
  comments: EditableMultipleChoiceProps['comments'];
  blockLabel: MultipleChoiceBlock['label'];
  onChangeComments: EditableMultipleChoiceProps['onChangeComments'];
};

const renderCommentsButton = ({
  comments,
  blockLabel,
  onChangeComments,
}: RenderCommentsProps): JSX.Element => (
  <CommentButton
    comments={comments}
    blockLabel={blockLabel}
    onChangeComments={onChangeComments}
  />
);

const renderClearSelection = (
  onChange: EditableMultipleChoiceProps['onChange']
): JSX.Element => (
  <NakedButton onClick={() => onChange(null)}>
    Clear above selection
  </NakedButton>
);

const RadioWrapper = (props: { label: string; children: ReactNode }) => {
  const { label, children } = props;
  return (
    <li className={styles.listItem}>
      <label className={styles.optionLabel}>
        {children}
        <span className={styles.optionLabelText}>{label}</span>
      </label>
    </li>
  );
};

const EditableRadio = (props: EditableMultipleChoiceProps) => {
  const { block, value } = props;

  return (
    <>
      {block.options.map((option) => (
        <RadioWrapper key={option.id} label={option.label}>
          <input
            type="radio"
            name={props.block.id}
            value={option.id}
            className={styles.optionInput}
            checked={option.id === value}
            onChange={() => {
              props.onChange(option.id);
            }}
            onClick={() => {
              props.onClickOption?.(option.id);
            }}
          />
        </RadioWrapper>
      ))}
    </>
  );
};

const ReadOnlyRadio = (props: ReadonlyMultipleChoiceProps) => {
  return (
    <>
      {props.block.options.map((option) => (
        <RadioWrapper key={option.id} label={option.label}>
          <input
            type="radio"
            name={props.block.id}
            value={option.id}
            className={styles.optionInput}
            disabled={true}
          />
        </RadioWrapper>
      ))}
    </>
  );
};

const OtherTextField = (props: {
  value?: string;
  disabled: boolean;
  name: string;
  className: string;
  onFocus?: () => void;
  onChange?: (newValue: string | null) => void;
  'aria-describedby'?: string;
}) => {
  const noop = () => '';
  const { onChange = noop, onFocus = noop } = props;
  const [fieldValue, setFieldValue] = useState(props.value);
  const debouncedCallback = useDebouncedFunction(onChange, 300);

  useEffect(() => {
    setFieldValue(props.value);
  }, [props.value]);

  return (
    <TextInput
      {...props}
      aria-label="Other - please specify"
      value={fieldValue}
      onFocus={onFocus}
      onChange={(event) => {
        setFieldValue(event.target.value);
        debouncedCallback(event.target.value);
      }}
    />
  );
};

export const MultipleChoice = (props: MultipleChoiceProps): JSX.Element => {
  const [descriptionID, describedBy] = useDescribedBy();
  const isEditable = isEditableMultipleChoiceProps(props);

  const [selectedValue, setSelectedValue] = useState<string | null>(
    (isEditable && props.value) || null
  );

  const editableValue = isEditable ? props.value : null;
  useEffect(() => {
    setSelectedValue(editableValue);
  }, [editableValue]);

  const callback = isEditable ? props.onChange : () => '';
  const debounced = useDebouncedFunction(callback, 300);

  const hideClearSelection =
    isEditable && (props as EditableMultipleChoiceProps).hideClearSelection;

  const ariaDescribedBy = [
    describedBy['aria-describedby'],
    props.radioGroupDescribedBy,
  ]
    .filter(Boolean)
    .join(' ');

  const radioList = (
    <ol
      className={styles.list}
      role="radiogroup"
      aria-describedby={ariaDescribedBy}
    >
      {isEditable ? (
        <EditableRadio
          {...props}
          value={selectedValue}
          onChange={(newValue) => {
            setSelectedValue(newValue);
            debounced(newValue);
          }}
        />
      ) : (
        <ReadOnlyRadio {...props} />
      )}
      {props.block.otherOption && (
        <li key={props.block.otherOption.id} className={styles.listItem}>
          <div className={styles.optionLabel}>
            <label>
              <input
                type="radio"
                name={props.block.id}
                value={props.block.otherOption.id}
                className={styles.optionInput}
                {...(isEditable
                  ? {
                      checked: isOtherOptionSelected(props.block, props.value),
                      onClick: () => {
                        props.onOtherFieldClick?.();
                      },
                      onChange: () => {
                        setSelectedValue('');
                        debounced('');
                      },
                    }
                  : {
                      disabled: true,
                    })}
              />

              <span className={styles.optionLabelText}>Other</span>
            </label>

            <OtherTextField
              name={`${props.block.id}_text`}
              className={styles.otherOptionText}
              aria-describedby={props.otherTextFieldDescribedBy}
              {...(isEditable
                ? {
                    disabled: !isOtherOptionSelected(props.block, props.value),
                    onChange: props.onChange,
                    onFocus: props.onOtherFieldFocus,
                    value: isOtherOptionSelected(props.block, props.value)
                      ? props.value || ''
                      : '',
                  }
                : {
                    disabled: true,
                  })}
            />
          </div>
        </li>
      )}
    </ol>
  );

  const wrappedList = props.radioGroupClassName ? (
    <div className={props.radioGroupClassName}>{radioList}</div>
  ) : (
    radioList
  );

  return (
    <>
      <Label>
        <div className={styles.labelOuter}>
          <div>
            <LabelText>{props.block.label}</LabelText>
            {props.block.description !== '' && (
              <div id={descriptionID} className={styles.description}>
                <MultilinedText>{props.block.description}</MultilinedText>
              </div>
            )}
          </div>
          {isEditable &&
            renderCommentsButton({
              comments: props.comments,
              blockLabel: props.block.label,
              onChangeComments: props.onChangeComments,
            })}
        </div>
      </Label>
      {props.trailingContent ? (
        <Inline spacing={2} alignY="top">
          {wrappedList}
          {props.trailingContent}
        </Inline>
      ) : (
        wrappedList
      )}
      {isEditable &&
        !hideClearSelection &&
        renderClearSelection((value) => {
          setSelectedValue(value);
          props.onChange(value);
        })}
    </>
  );
};
